import { FC, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnyTask, Market, TaskRoute } from "tm-shared/types";

import { TaskItemView } from "@/components/ItemListView/MarketTaskItemView";
import { ROUTES } from "@/constants";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { addModalToQueue } from "@/store/slices/ui";
import { ModalType } from "@/types/modals";
import { isTaskRoute } from "@/utils";

import { TaskProgressModal } from "../TaskProgressModal";

const OFFLINE_ENABLED_ROUTES: TaskRoute[] = [
    TaskRoute.StartVisit,
    TaskRoute.SaleAdditional,
    TaskRoute.RejectProducts,
    TaskRoute.TakeDebt,
    TaskRoute.ShelfSize,
    TaskRoute.SetGeoLocation,
    TaskRoute.UpdateProductBalances,

    TaskRoute.AssortmentMatrix,
    TaskRoute.Dmp,
    TaskRoute.PromoTagPhotos,
    TaskRoute.AddPhoto,

    TaskRoute.UpdateMarketInfo,
    TaskRoute.CustomTask,
];

const offlineEnabledRoutesSet = new Set<TaskRoute>(OFFLINE_ENABLED_ROUTES);

const BLOCKERS: Array<{ blocker: TaskRoute; allowed: Set<TaskRoute> }> = [
    {
        blocker: TaskRoute.StartVisit,
        allowed: new Set([TaskRoute.StartVisit]),
    },
    {
        blocker: TaskRoute.SetGeoLocation,
        allowed: new Set([TaskRoute.SetGeoLocation, TaskRoute.StartVisit]),
    },
];

interface MarketTasksProps {
    tasks: AnyTask[];
    market: Market;
    onOpenTasksInfoModal: (params: { task: AnyTask; market: Market }) => void;
    incompleteRoutes: Set<TaskRoute>;
    readonly?: boolean;
}

export const MarketTasks: FC<MarketTasksProps> = ({
    tasks,
    market,
    readonly = false,
    incompleteRoutes,
    onOpenTasksInfoModal,
}) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { requestQueue, offlineMode } = useAppSelector((s) => s.offline);
    const progress = useAppSelector((s) => s.progress);

    const [selectedQueueItemId, setSelectedQueueItemId] = useState<
        string | null
    >(null);

    const isTaskEnabled = useCallback(
        (task: AnyTask): boolean => {
            const route = task.context.route;

            const gate = BLOCKERS.find((g) => incompleteRoutes.has(g.blocker));
            if (gate && !gate.allowed.has(route)) return false;

            if (offlineMode && !offlineEnabledRoutesSet.has(route))
                return false;

            return true;
        },
        [incompleteRoutes, offlineMode],
    );

    const handleTaskClick = useCallback(
        (task: AnyTask) => {
            if (readonly || !isTaskEnabled(task)) return;

            const queueItem = requestQueue.find((item) => item.id === task.id);

            // якщо завершена і в черзі на синхронізацію
            if (task.completed && queueItem) {
                setSelectedQueueItemId(queueItem.id);
                return;
            }

            // якщо онлайн і дані в черзі на синхронізацію
            if (queueItem && !offlineMode) {
                setSelectedQueueItemId(queueItem.id);
                return;
            }

            // якщо завершена і не в черзі
            if (task.completed) {
                onOpenTasksInfoModal({ task, market });
                return;
            }

            const route = task.context.route;

            if (route === TaskRoute.StartVisit) {
                dispatch(
                    addModalToQueue({
                        type: ModalType.StartVisit,
                        id: `${ModalType.StartVisit}-${task.id}`,
                        payload: { market, taskId: task.id },
                    }),
                );

                return;
            }

            // if (route in TaskRoute) {
            if (isTaskRoute(route)) {
                navigate(
                    `${ROUTES.WorkPlan}/${task.targetId}/${task.context.route}/${task.id}`,
                );
                return;
            }

            console.error("No handler for route:", route, task);
        },
        [
            readonly,
            isTaskEnabled,
            requestQueue,
            offlineMode,
            dispatch,
            market,
            navigate,
        ],
    );

    const selectedQueueItem =
        requestQueue.find((item) => item.id === selectedQueueItemId) ?? null;

    return (
        <>
            {tasks.map((task) => {
                const isAccent = task.context.route === TaskRoute.CustomTask;

                const queueItem = requestQueue.find(
                    (item) => item.id === task.id,
                );

                const status = queueItem?.status ?? null;

                return (
                    <TaskItemView
                        key={task.id}
                        task={task}
                        onTaskClick={handleTaskClick}
                        isTaskEnabled={isTaskEnabled}
                        isTaskDirty={
                            !!progress[task.context.route]?.[task.id] &&
                            !task.completed
                        }
                        accent={isAccent}
                        status={status}
                    />
                );
            })}

            {!!selectedQueueItem && (
                <TaskProgressModal
                    queueItem={selectedQueueItem}
                    onClose={() => setSelectedQueueItemId(null)}
                />
            )}
        </>
    );
};
